import { NavigateFn } from '@tanstack/react-router'
import { Column, ColumnDef, ColumnFiltersState } from '@tanstack/react-table'
import { Spin } from 'antd'
import AssetsIcon from 'assets/assets-tab-icon.svg?react'
import { AddButton } from 'components/CantileverButton'
import { ErrorScreen } from 'components/ErrorScreen'
import { PageHeader } from 'components/PageHeader'
import { AssetOverviewAdminGate } from 'features/assetDatabase/components/gates/AssetOverviewGates'
import { useAssetCardsData } from 'features/assetDatabase/hooks/useAssetCardsData'
import { useLandingPageData } from 'features/assetDatabase/hooks/useLandingPageData'
import { AssetCardProps } from 'features/assetDatabase/types/assetCardTypes'
import {
  AssetData,
  AssetTableEntries,
  AssetTableEntry,
  UnitAssetModelTable,
  UnitInspections,
  UnitPhotogrammetryModel,
} from 'features/assetDatabase/types/assetTableTypes'
import { DrawerState, useRightDrawer } from 'features/rightDrawer'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { GeckoSite, RolodexEntryType } from 'types'

import { AssetTable } from './AssetTable'
import {
  assetInspections,
  assetPhotogrammetryModels,
  createAssetTableData,
  getAssetModels,
} from './AssetTableHelpers'

interface AssetTableDataProps {
  site: GeckoSite | undefined
  siteID: string
  onTableStateChange?: (newState: Record<string, unknown>) => void
  initialTableState?: Record<string, unknown>
  // Customization props
  title?: string
  icon?: React.ReactNode
  showAddButton?: boolean
  overrideColumns?: ColumnDef<AssetData>[]
  customDataTransformer?: (
    unitAssets: AssetTableEntry[],
    unitAssetModels: AssetTableEntry[],
    unitInspections: UnitInspections[],
    cardProps: AssetCardProps[]
  ) => AssetData[]
  footerName?: string
  routeFrom?: string
  initialSorting?: { id: string; desc: boolean }[]
  customTopBar?: (props: {
    columns: ColumnDef<AssetData>[]
    setGlobalFilter: React.Dispatch<unknown>
    getColumn: (columnID: string) => Column<AssetData> | undefined
    initialColumnFilters?: ColumnFiltersState
    navigate: NavigateFn
    disableUrlFilters?: boolean
  }) => React.ReactNode
}

const StyledAssetsIcon = styled(AssetsIcon)`
  width: 1.5rem;
  height: 1.5rem;
  padding: 0.25rem;

  * {
    fill: ${({ theme }) => theme.forecast.defaultText};
  }
`

export const AssetTableData: React.FC<AssetTableDataProps> = (props) => {
  const {
    siteID,
    onTableStateChange,
    initialTableState,
    title = 'Assets',
    icon,
    showAddButton = true,
    overrideColumns,
    customDataTransformer,
    footerName = 'Asset(s)',
    routeFrom,
    initialSorting,
    customTopBar,
  } = props
  const { setDrawerState, setDrawerEntry, setOnDrawerClose } = useRightDrawer()

  const {
    assetModels,
    units,
    components,
    componentDataFrames,
    componentDatasets,
    componentDefaults,
    isLoading: isLoadingPage,
  } = useLandingPageData({ siteID })
  const {
    data: assetData,
    isLoading: isLoadingData,
    mutate,
  } = useGetRolodexEntriesByLoadOptions<AssetTableEntries[]>(
    React.useMemo(
      () => ({
        type: [
          RolodexEntryType.UNIT,
          RolodexEntryType.ASSET_MODEL,
          RolodexEntryType.COMPONENT,
          RolodexEntryType.INSPECTION,
          RolodexEntryType.COMPONENT_DEFAULTS,
          RolodexEntryType.PHOTOGRAMMETRY_MODEL,
          RolodexEntryType.REPORT,
        ],
        tags: { site: siteID },
        order_by: [{ key: 'last_updated', descending: 'true' }],
        limit: 1000000,
      }),
      [siteID]
    )
  )

  const cardProps = useAssetCardsData(
    assetModels,
    units,
    components,
    componentDataFrames,
    componentDefaults,
    componentDatasets
  )

  const unitAssets = assetData
    ?.filter((asset) => asset.entry.type === 'unit')
    .map((unit) => unit.entry)

  const unitComponents = assetData
    ?.filter((asset) => asset.entry.type === 'component')
    .map((unit) => unit.entry.tags.unit)

  const unitAssetModels: UnitAssetModelTable[] | undefined =
    getAssetModels(assetData)

  const unitInspections: UnitInspections[] | undefined =
    assetInspections(assetData)

  const unitPhotogrammetryModels: UnitPhotogrammetryModel[] | undefined =
    assetPhotogrammetryModels(assetData)

  const unitFiles = assetData
    ?.filter((asset) => asset.entry.type === RolodexEntryType.REPORT)
    .map((file) => file.entry.tags.unit)

  const data: AssetData[] = useMemo(() => {
    if (customDataTransformer) {
      const assetModelEntries =
        assetData
          ?.filter((asset) => asset.entry.type === 'asset-model')
          .map((asset) => asset.entry) ?? []

      return unitAssets && unitInspections
        ? customDataTransformer(
            unitAssets,
            assetModelEntries,
            unitInspections,
            cardProps
          )
        : []
    }

    // Default behavior
    return unitAssets &&
      unitAssetModels &&
      unitComponents &&
      unitInspections &&
      unitPhotogrammetryModels &&
      unitFiles
      ? createAssetTableData(
          unitAssets,
          unitAssetModels,
          unitComponents,
          unitInspections,
          unitPhotogrammetryModels,
          cardProps,
          unitFiles
        )
      : []
  }, [
    customDataTransformer,
    unitAssets,
    assetData,
    unitAssetModels,
    unitComponents,
    unitFiles,
    unitInspections,
    unitPhotogrammetryModels,
    cardProps,
  ])
  const errorScreenMessage =
    'Please contact support for the proper permissions.'

  const displayIcon = icon || <StyledAssetsIcon />

  return (
    <>
      <PageHeader
        title={title}
        icon={displayIcon}
        trailingContent={
          showAddButton ? (
            <AssetOverviewAdminGate>
              <AddButton
                entityName='Asset'
                onClick={() => {
                  if (siteID) {
                    setDrawerEntry(props.site)
                    setDrawerState(DrawerState.EditGeckoUnit)
                    setOnDrawerClose(() => mutate)
                  }
                }}
              />
            </AssetOverviewAdminGate>
          ) : undefined
        }></PageHeader>
      <>
        {isLoadingData || isLoadingPage ? (
          <Spin spinning={true} />
        ) : !siteID ? (
          <ErrorScreen
            title={'Site was not properly loaded'}
            message={errorScreenMessage}
          />
        ) : data && data.length > 0 ? (
          <AssetTable
            siteID={siteID}
            isLoading={isLoadingData}
            data={data}
            onTableStateChange={onTableStateChange}
            initialTableState={initialTableState}
            overrideColumns={overrideColumns}
            footerName={footerName}
            routeFrom={routeFrom}
            initialSorting={initialSorting}
            customTopBar={customTopBar}
          />
        ) : (
          <ErrorScreen
            title={'No Assets for the Site'}
            message={errorScreenMessage}
          />
        )}
      </>
    </>
  )
}
